import { RoleGroupModel } from "@app/admin/role-groups/roleGroup";
import { GuestLoginAdminModel } from "@app/user/guestLogin";
import RoleModel from "@app/user/roleModel";

interface UserModelBase {
  // user info
  userName: string;
  firstName: string;
  middleInitial: string;
  lastName: string;
  jobRoleId?: number;
  jobRole?: string;
  jobTitle?: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  email: string;
  sFContactId: string;
  phoneNumber: string;
  questViewerGuestLoginsAllowed?: boolean;
  resourceAdminGuestLoginsAllowed?: boolean;

  // single dropdown
  clientId: number;
  clientName: string;
}

export interface NewUserModel extends UserModelBase {
  // the authentication apiaccountcontroller expects a hashset<string> of role names
  // so we send a string array here instead of RoleModels
  guestLogins: GuestLoginAdminModel[];
  roleGroupIds: number[];
  roleNames: string[];
  jobRoleId?: number;
  jobRole?: string;
  jobTitle: string;
}

interface ExistingUserModel extends UserModelBase {

  // used/editable
  passwordExpirationEnabled: boolean;
  loginPermitted: boolean;

  // not editable
  userId: number;
  userNameIsSameAsEmail: boolean;

  // derived properties
  fullName?: string; // first middle last
  formalName?: string; // last, first middle
  shortFormalName?: string;

  // informational, not editable 
  emailConfirmed: boolean;
}

// we get RoleModel[] from the api and it wants string[] back...
export interface UserModelFromApi extends ExistingUserModel {
  roleGroups: RoleGroupModel[];
  roles: RoleModel[];
  guestLogins: GuestLoginAdminModel[];
  deletedGuestLogins: GuestLoginAdminModel[];
  status?: UserStatus;
  auditTrail: UserAuditTrailModel[];
  jobRoleId?: number;
  jobRole?: string;
  jobTitle?: string;
}

export interface UserModelToApi extends ExistingUserModel {
  addedRoleGroupIds: number[];
  addedRoleNames: string[];
  changedGuestLogins: GuestLoginAdminModel[];
  removedRoleGroupIds: number[];
  removedRoleNames: string[];
  jobRoleId?: number;
  jobRole?: string;
  jobTitle?: string;
}

export enum UserAuditType {
  UNKNOWN = "unknown",
  CREATED = "created",
  EMAILCONFIRMED = "emailConfirmed",
  EMAILCHANGED = "emailChanged",
  EMAILCHANGECONFIRMED = "emailChangeConfirmed",
  USERNAMECHANGED = "userNameChanged",
  ACTIVATED = "activated",
  DEACTIVATED = "deactivated",
  DELETED = "deleted",
  UNDELETED = "undeleted",
  EXPIRED = "expired"
}

export interface UserAuditTrailModel {
  userAuditId: number;
  auditType: UserAuditType;
  auditDate: Date;
  auditedByUserName: string;
  auditRemarks: string;
}

export function toDisplayAuditType(auditType: UserAuditType): string {
  switch (auditType) {
    case UserAuditType.CREATED:
      return "Account Created";
    case UserAuditType.EMAILCONFIRMED:
      return "Email Confirmed";
    case UserAuditType.EMAILCHANGED:
      return "Email Changed";
    case UserAuditType.EMAILCHANGECONFIRMED:
      return "Email Change Confirmed";
    case UserAuditType.USERNAMECHANGED:
      return "UserName Changed";
    case UserAuditType.ACTIVATED:
      return "Account Activated";
    case UserAuditType.DEACTIVATED:
      return "Account Deactivated";
    case UserAuditType.DELETED:
      return "Account Deleted";
    case UserAuditType.UNDELETED:
      return "Account Un-Deleted";
    case UserAuditType.EXPIRED:
      return "Account Expired";
    default:
      throw new Error("Unexpected UserAuditType: " + auditType);
  }
}

// UserStatus is a bitfield, values can be combined together to select multiple statuses for filtering in the UI
// A given user, however, will only have one of these values.
export enum UserStatus {
  UNKNOWN = 0,
  ACTIVATED = 0x01,
  UNCONFIRMED = 0x02,
  DEACTIVATED = 0x04,
  DELETED = 0x08,
}

export interface UserClaim {
  type: string;
  value: string;
}

export interface JobRoleTypeModel {
  jobRoleId: number;
  jobRole: string;
  isActive: boolean;
  createdDate: Date;
  modifiedDate: Date;
}